import { TableCell, TableRow } from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import Badge from '@/components/shared/Badge/Badge'
import { ArrowLeftRight } from 'lucide-react'

import AbaSaques from '@/assets/icons/AbaSaques'
import { useTranslation } from 'react-i18next'
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom'
import {
  getWithdrawalListAmountFormatted,
  hasWithdrawalFxConversion,
  WithdrawalFxRecord,
  WithdrawalResponse,
} from '@/services/wallet.service'

interface BankDetail {
  id: string
  pix_key_type?: string
  pix_key?: string

  // BRL / conta bancária nacional
  bank_code?: string
  agency_number?: string
  agency_digit?: string
  account_number?: string
  account_digit?: string

  // USD / conta internacional (ex: ACH)
  bank_name?: string
  routing_number?: string
  bank_country?: string

  // USD / Virtual (Payoneer, Wise, etc)
  provider?: string
  virtual_account_id?: string
  payoneer_email?: string
  rail?: string
}

interface Withdrawal {
  id: string
  currency?: string
  bankDetail: BankDetail
  usdBankDetail?: BankDetail
  status: string
  payout_status?: string | null
  payout_status_label?: string | null
  amount_requested: number
  amount_requested_formatted: string
  refused_description: string | null
  created_at: string
  created_at_formatted: string
  updated_at: string
  fx?: WithdrawalFxRecord | null
}

// Status do saque que sao DECISAO da Mundpay: nao dependem de transferencia nenhuma, e por isso
// vencem o estado do payout. Um saque recusado continua recusado mesmo que nenhum payout tenha
// sido iniciado — era o que fazia 1.268 saques recusados aparecerem como "processando" para o
// produtor, com o motivo da recusa ao lado, enquanto o admin mostrava "Rejeitada".
const TERMINAL_WITHDRAWAL_STATUSES: string[] = ['rejected', 'refunded']

/**
 * Qual status mostrar na lista do produtor.
 *
 * O payout so pode SEGURAR o "concluido" de um saque que ja transferiu: enquanto existe payout em
 * andamento, dizer "concluido" seria prometer dinheiro que ainda nao saiu. Fora disso, quem manda
 * e o status do saque.
 *
 * `not_initiated` NAO e payout em andamento: e a ausencia de payout. Saque pago por transferencia
 * manual, ou anterior ao fluxo de payout, fica nesse estado para sempre — sao 8.150 registros,
 * nenhum com tentativa de payout, e todos apareciam como "processando" apesar de o vendedor ter
 * recebido. A guarda antiga (`!payoutStatus`) nunca pegava esse caso porque a coluna nao vem nula,
 * vem `not_initiated`.
 */
export function resolveWithdrawalDisplayStatus(withdrawal: Withdrawal): string {
  if (TERMINAL_WITHDRAWAL_STATUSES.includes(withdrawal.status)) {
    return withdrawal.status
  }

  if (withdrawal.payout_status === 'failed') {
    return 'failed'
  }

  if (withdrawal.payout_status === 'pending') {
    return 'processing'
  }

  return withdrawal.status
}

interface Props {
  withdrawals?: WithdrawalResponse
  isLoading: boolean
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export default function WithdrawalList({
  withdrawals,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
}: Props) {
  const { t } = useTranslation(['wallet', 'onboarding'])
  const withdrawalsData = withdrawals ?? { data: [] }

  const headers = [
    { title: t('date'), key: 'date' },
    { title: t('value'), key: 'value' },
    { title: t('accountPix'), key: 'account' },
    { title: t('status'), key: 'status' },
    { title: t('refuse_description'), key: 'refused_description' },
  ]

  const getAccountDetail = (withdrawal: Withdrawal, t: any): string => {
    const isUsd = (withdrawal.currency || '').toUpperCase() === 'USD'
    const bankDetail = isUsd
      ? withdrawal.usdBankDetail ?? withdrawal.bankDetail
      : withdrawal.bankDetail

    if (bankDetail?.pix_key) {
      return t('accountDetails.pixFormat', {
        type: bankDetail.pix_key_type,
        key: bankDetail.pix_key,
      })
    }

    if (isUsd) {
      // Conta virtual (Payoneer, Wise, etc.)
      if (bankDetail?.provider) {
        const providerName = bankDetail.provider.charAt(0).toUpperCase() + bankDetail.provider.slice(1)
        if (bankDetail.payoneer_email) {
          return `${providerName} - ${bankDetail.payoneer_email}`
        }
        return `${providerName} - ${bankDetail.virtual_account_id || ''}`
      }

      // Conta ACH/WIRE/SWIFT
      return `${t('onboarding:bank')}: ${bankDetail.bank_name || '-'} - ${t(
        'onboarding:routing_number',
      )}: ${bankDetail.routing_number || '-'}`
    }

    return t('accountDetails.bankDetails', {
      bankCode: bankDetail?.bank_code,
      agencyNumber: bankDetail?.agency_number,
      agencyDigit: bankDetail?.agency_digit,
      accountNumber: bankDetail?.account_number,
      accountDigit: bankDetail?.account_digit,
    })
  }

  return (
    <Card>
      <div className="px-4 py-5 border-b border-b-colorBorder">
        <h3 className="text-base font-semibold">{t('withdrawalListTitle')}</h3>
      </div>

      <TableListCustom
        emptyDescriptionMessage={t('noWithdrawalsDescription')}
        emptyIconMessage={<AbaSaques />}
        emptyTitleMessage={t('noWithdrawalsTitle')}
        headers={headers}
        isLoadingData={isLoading}
        data={withdrawalsData}
        currentPage={currentPage}
        setCurrentPage={onPageChange}
      >
        {withdrawalsData.data?.map((withdrawal) => (
          <TableRow
            key={withdrawal.id}
            className="hover:bg-transparent border-t border-zinc-800 whitespace-nowrap"
          >
            <TableCell className="py-4 text-[#A1A1AA]">
              {withdrawal.created_at_formatted}
            </TableCell>

            <TableCell className="py-4">
              <div className="flex flex-col gap-1">
                <span className="font-medium">
                  {getWithdrawalListAmountFormatted(withdrawal)}
                </span>
                {hasWithdrawalFxConversion(withdrawal) && withdrawal.fx && (
                  <div className="flex flex-col gap-0.5">
                    <span
                      className="inline-flex w-fit items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary"
                      data-cy="indicador-conversao-saque"
                    >
                      <ArrowLeftRight className="h-3 w-3 shrink-0" />
                      {t('withdrawal_fx.list_badge')}
                      {withdrawal.fx.source_currency && withdrawal.fx.payout_currency && (
                        <span className="text-primary/80">
                          {withdrawal.fx.source_currency} → {withdrawal.fx.payout_currency}
                        </span>
                      )}
                    </span>
                    {withdrawal.fx.amount_source_formatted &&
                      withdrawal.fx.amount_converted_formatted && (
                        <span className="text-xs text-zinc-500">
                          {t('withdrawal_fx.list_amount_detail', {
                            source: withdrawal.fx.amount_source_formatted,
                            converted: withdrawal.fx.amount_converted_formatted,
                          })}
                        </span>
                      )}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell className="py-4">
              <div className="flex flex-col">
                <span className="font-medium">
                  {withdrawal.bankDetail?.pix_key
                    ? 'PIX'
                    : withdrawal.usdBankDetail?.provider
                      ? withdrawal.usdBankDetail.provider.charAt(0).toUpperCase() + withdrawal.usdBankDetail.provider.slice(1)
                      : t('bankAccount')}
                </span>
                <span className="text-sm text-zinc-400">
                  {getAccountDetail(withdrawal, t)}
                </span>
              </div>
            </TableCell>
            <TableCell className="py-4 ">
              <Badge status={resolveWithdrawalDisplayStatus(withdrawal)} />
            </TableCell>
            <TableCell className="py-4 text-[#A1A1AA]">
              {withdrawal.refused_description || '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableListCustom>
    </Card>
  )
}
